/**
 * NotificationBell — bell icon with unread badge + dropdown panel.
 *
 * Drop into the main header: <NotificationBell user={user} />
 *
 * RW-1685 (v3 deck ab-09 "Notifications · In-app"): rows restyled to the
 * deck's voice — actor avatar (fromPhoto/fromName, usersById fallback) or a
 * tinted system glyph (road-block "!" err-tint, due "◯" gold-tint; every
 * other server type keeps its emoji in its own tint), one-sentence body with
 * the bold entity leading, right-aligned "9:14a" time, unread = coral-soft
 * row + left coral dot, "Mark all read" as the dashed ghost, coral
 * "View all →". The All | @ Mentions tabs stay (RW-1681 scope decision —
 * the deck predates them). NotificationsPage grouping untouched.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { batch as dataBatch, query as dataQuery, subscribe as dataSubscribe, serverTimestamp, update } from '../services/data';
import { NOTIFICATION_TYPES, getNotificationDisplayTitle, getThreadKey, isSnoozed } from '../lib/notifications';
import { collapsedLabel, prioritizeNotifications } from '../lib/notificationPriority';
import { isValidInternalLink } from '../lib/validateLink';
import { useToast } from '../contexts/ToastContext';
import { useTenant } from '../contexts/TenantContext';
import { useSharedUsers } from '../contexts/UsersContext';
import { colors, fonts } from './theme';
import { usablePhotoURL } from '../lib/avatarPhoto';

/** "9:14a" today, "Tue 9:14a" inside a week, "Jul 27" beyond — the deck's
 *  timestamp voice (OnboardingTaskDrawer.when). */
function when(v) {
  const d = v && typeof v.toDate === 'function' ? v.toDate() : (v ? new Date(v) : null);
  if (!d || Number.isNaN(d.getTime())) return '';
  const days = (Date.now() - d.getTime()) / 86400000;
  const t = d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })
    .toLowerCase().replace(' ', '').replace('m', ''); // 9:14a
  if (days < 1 && d.getDate() === new Date().getDate()) return t;
  if (days < 7) return `${d.toLocaleDateString(undefined, { weekday: 'short' })} ${t}`;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

/* ── ab-09 leading mark: actor avatar or tinted system glyph ─────────── */

const AV = { width: 26, height: 26, borderRadius: '50%', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' };

/** Due-reminder types → the deck's "◯" gold glyph. */
const DUE_TYPES = new Set(['task_due', 'task_overdue', 'overdue_items', 'onboarding_nudge']);

/** Actor avatar (photo → initials) when the notification names a real person;
 *  else the type glyph. Exported shape kept local — this is presentation only. */
function LeadMark({ n, usersById }) {
  const actor = n.fromUid && n.fromUid !== 'system' ? usersById[n.fromUid] : null;
  // RW-3825: a stock "no photo" image counts as no photo
  const photo = usablePhotoURL(n.fromPhoto) || usablePhotoURL(actor && actor.photoURL);
  const name = n.fromName || (actor && (actor.displayName || actor.name)) || null;
  const [imgErr, setImgErr] = useState(false);

  if (photo && !imgErr) {
    return <img src={photo} alt="" title={name || ''} onError={() => setImgErr(true)} style={{ ...AV, objectFit: 'cover' }} />;
  }
  if (name) {
    const initials = name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join('').toUpperCase();
    return (
      <span style={{ ...AV, background: colors.coral, color: '#fff', fontSize: 10.5, fontWeight: 700 }} title={name} aria-hidden="true">
        {initials || '—'}
      </span>
    );
  }
  // System rows — deck ab-09: road block "!" err-tint, due "◯" gold-tint;
  // everything else keeps its NOTIFICATION_TYPES emoji in its own tint.
  const text = `${n.title || ''} ${n.body || ''}`;
  if (n.type === 'task_status_changed' && /block/i.test(text)) {
    return <span style={{ ...AV, background: '#FCE9E6', color: '#9A392B', fontSize: 12, fontWeight: 700 }} aria-hidden="true">!</span>;
  }
  if (DUE_TYPES.has(n.type)) {
    return <span style={{ ...AV, background: colors.goldSoft, color: '#8A6D1F', fontSize: 12, fontWeight: 700 }} aria-hidden="true">◯</span>;
  }
  const meta = NOTIFICATION_TYPES[n.type] || NOTIFICATION_TYPES.info;
  return <span style={{ ...AV, background: `${meta.color}20`, fontSize: 13 }} aria-hidden="true">{meta.icon}</span>;
}

export default function NotificationBell({ user }) {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { tenantId } = useTenant();
  const { usersById } = useSharedUsers();
  const [items, setItems] = useState([]);
  const [mutedThreads, setMutedThreads] = useState([]);
  const [nowMs, setNowMs] = useState(() => Date.now());
  const [open, setOpen] = useState(false);
  // RW-1685 — 'all' | 'mentions'. Mentions is its OWN query (not a client
  // filter): the base query is limit(30), so filtering it would only ever see
  // mentions that happen to be in the newest 30. Needs the composite index
  // (tenantId, recipientUid, type, createdAt desc) — added with this ticket.
  const [tab, setTab] = useState('all');
  // RW-2265: folded system rows ("3 overdue task reminders") the reader opened.
  const [expandedGroups, setExpandedGroups] = useState(() => new Set());
  const ref = useRef(null);

  useEffect(() => {
    if (!user?.uid || !tenantId) return;
    // Note: this query needs a composite index on (tenantId, recipientUid, createdAt desc).
    // Firestore will suggest creating it on first run if missing.
    return dataQuery('notifications')
      .where('tenantId', '==', tenantId)
      .where('recipientUid', '==', user.uid)
      .orderBy('createdAt', 'desc')
      .limit(30)
      .subscribe(
        (rows) => setItems(rows),
        (err) => { console.warn('notifications query failed:', err?.message); },
      );
  }, [user?.uid, tenantId]);

  // RW-1685 — the Mentions tab's own subscription, live only while selected
  // (the badge stays on the ALL set above, so it never undercounts).
  const [mentionItems, setMentionItems] = useState([]);
  useEffect(() => {
    if (!user?.uid || !tenantId || tab !== 'mentions') return;
    return dataQuery('notifications')
      .where('tenantId', '==', tenantId)
      .where('recipientUid', '==', user.uid)
      .where('type', '==', 'mention')
      .orderBy('createdAt', 'desc')
      .limit(30)
      .subscribe(
        (rows) => setMentionItems(rows),
        (err) => { console.warn('mentions query failed:', err?.message); },
      );
  }, [user?.uid, tenantId, tab]);

  // Muted threads live on the user doc (written by NotificationsPage).
  useEffect(() => {
    if (!user?.uid) return undefined;
    return dataSubscribe(`users/${user.uid}`, (doc) => {
      setMutedThreads(Array.isArray(doc?.notificationMutedThreads) ? doc.notificationMutedThreads : []);
    }, () => {});
  }, [user?.uid]);

  // Keep snooze comparisons fresh so snoozed items reappear on time.
  useEffect(() => {
    const t = setInterval(() => setNowMs(Date.now()), 60000);
    return () => clearInterval(t);
  }, []);

  // Snoozed + muted-thread notifications shouldn't ping the bell — keep it in
  // sync with the inbox (NotificationsPage). Snooze is read straight off the
  // doc; mutes live on the user doc (subscribed below).
  const mutedSet = useMemo(() => new Set(mutedThreads), [mutedThreads]);
  const visible = useMemo(
    () => items.filter((n) => {
      const tk = getThreadKey(n);
      return !(tk && mutedSet.has(tk)) && !isSnoozed(n, nowMs);
    }),
    [items, mutedSet, nowMs],
  );
  const unreadCount = useMemo(() => visible.filter((n) => !n.read).length, [visible]);

  // Close dropdown on outside click
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  const markAsRead = useCallback(async (id) => {
    try { await update('notifications', id, { read: true, readAt: serverTimestamp() }); }
    catch (e) {
      console.warn('mark read failed', e);
      showToast('Could not mark that as read. Please try again.', 'error');
    }
  }, [showToast]);

  const markAllRead = useCallback(async () => {
    const unread = visible.filter((n) => !n.read);
    if (unread.length === 0) return;
    const b = dataBatch();
    unread.forEach((n) => b.update('notifications', n.id, { read: true, readAt: serverTimestamp() }));
    try { await b.commit(); } catch (e) {
      console.warn('mark all read failed', e);
      showToast('Could not mark all as read. Please try again.', 'error');
    }
  }, [visible, showToast]);

  const onClickItem = async (n) => {
    if (!n.read) markAsRead(n.id);
    setOpen(false);
    if (n.link) {
      if (isValidInternalLink(n.link)) {
        navigate(n.link);
      } else {
        console.warn('Invalid notification link:', n.link);
      }
    }
  };

  const visibleMentions = useMemo(
    () => mentionItems.filter((n) => {
      const tk = getThreadKey(n);
      return !(tk && mutedSet.has(tk)) && !isSnoozed(n, nowMs);
    }),
    [mentionItems, mutedSet, nowMs],
  );
  // RW-2265 (F5): people first — a reaction, comment, mention, recognition,
  // message or room invite a real person caused — then the system rows, with
  // consecutive runs of one type folded into a single expandable row. The
  // seed agent's bell opened on five identical "Onboarding Reminder" rows with
  // the one recognition they'd received out of sight.
  const rows = useMemo(
    () => prioritizeNotifications(tab === 'mentions' ? visibleMentions : visible).slice(0, 10),
    [tab, visibleMentions, visible],
  );
  const toggleGroup = (key) => setExpandedGroups((prev) => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  const renderItem = (n, { indent = false } = {}) => (
    <button key={n.id} onClick={() => onClickItem(n)}
      style={{
        position: 'relative', display: 'flex', width: '100%', gap: 10, alignItems: 'flex-start',
        padding: indent ? '9px 14px 9px 34px' : '11px 14px 11px 18px', border: 'none', cursor: 'pointer', textAlign: 'left',
        background: n.read ? '#fff' : colors.coralSoft,
        borderBottom: `1px solid ${colors.line2}`, fontFamily: fonts.body,
      }}
      onMouseEnter={(e) => { e.currentTarget.style.background = n.read ? colors.panel : '#FBE4E0'; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = n.read ? '#fff' : colors.coralSoft; }}>
      {/* unread — the deck's left coral dot */}
      {!n.read ? (
        <span aria-hidden="true" style={{ position: 'absolute', left: indent ? 23 : 7, top: 20, width: 6, height: 6, borderRadius: '50%', background: colors.coral }} />
      ) : null}
      <LeadMark n={n} usersById={usersById} />
      <div style={{ flex: 1, minWidth: 0, fontSize: 12.5, color: colors.text, lineHeight: 1.45 }}>
        <span style={{ fontWeight: 650, color: colors.text }}>{getNotificationDisplayTitle(n)}</span>
        {n.body ? <> — {n.body}</> : null}
      </div>
      <span style={{ flexShrink: 0, fontSize: 11, color: colors.mute2, marginTop: 2, fontVariantNumeric: 'tabular-nums' }}>{when(n.createdAt)}</span>
    </button>
  );

  const renderGroup = (row) => {
    const expanded = expandedGroups.has(row.key);
    const unread = row.items.filter((n) => !n.read).length;
    const label = collapsedLabel(row.type, row.count);
    return (
      <React.Fragment key={row.key}>
        <button type="button" onClick={() => toggleGroup(row.key)} aria-expanded={expanded} aria-label={`${label}, ${expanded ? 'hide' : 'show all'}`}
          style={{
            position: 'relative', display: 'flex', width: '100%', gap: 10, alignItems: 'flex-start',
            padding: '11px 14px 11px 18px', border: 'none', cursor: 'pointer', textAlign: 'left',
            background: unread ? colors.coralSoft : '#fff',
            borderBottom: `1px solid ${colors.line2}`, fontFamily: fonts.body,
          }}>
          {unread ? (
            <span aria-hidden="true" style={{ position: 'absolute', left: 7, top: 20, width: 6, height: 6, borderRadius: '50%', background: colors.coral }} />
          ) : null}
          {/* the type glyph, never an actor — these are the system's rows */}
          <LeadMark n={{ type: row.type, title: row.latest.title, body: row.latest.body }} usersById={usersById} />
          <div style={{ flex: 1, minWidth: 0, fontSize: 12.5, color: colors.text, lineHeight: 1.45 }}>
            <span style={{ fontWeight: 650, color: colors.text }}>{label}</span>
            <span style={{ color: colors.muted }}> — latest: {getNotificationDisplayTitle(row.latest)} · {expanded ? 'hide' : 'show all'}</span>
          </div>
          <span style={{ flexShrink: 0, fontSize: 11, color: colors.mute2, marginTop: 2, fontVariantNumeric: 'tabular-nums' }}>{when(row.latest.createdAt)}</span>
        </button>
        {expanded ? row.items.map((n) => renderItem(n, { indent: true })) : null}
      </React.Fragment>
    );
  };

  return (
    <div ref={ref} style={{ position: 'relative' }} role="region" aria-label="Notifications">
      <button onClick={() => setOpen((v) => !v)} aria-label={unreadCount > 0 ? `Notifications (${unreadCount} unread)` : 'Notifications'} title="Notifications"
        aria-expanded={open} aria-haspopup="true"
        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 8, position: 'relative', fontSize: 18, minWidth: 44, minHeight: 44, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        🔔
        {unreadCount > 0 ? (
          <span aria-hidden="true" style={{ position: 'absolute', top: 4, right: 4, background: colors.coral, color: '#fff', fontSize: 10, fontWeight: 700, borderRadius: 10, minWidth: 16, height: 16, padding: '0 4px', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 0 0 2px #fff' }}>
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        ) : null}
      </button>

      {open ? (
        <div role="dialog" aria-label="Notifications" style={{ position: 'absolute', top: '100%', right: 0, marginTop: 4, width: 364, maxWidth: '90vw', background: '#fff', border: `1px solid ${colors.line}`, borderRadius: 12, boxShadow: '0 1px 2px rgba(42,38,34,.05), 0 8px 24px rgba(42,38,34,.07)', zIndex: 1100, overflow: 'hidden', fontFamily: fonts.body }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '11px 14px', borderBottom: `1px solid ${colors.line}` }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <div style={{ fontSize: 13, fontWeight: 650, color: colors.text }}>Notifications</div>
              {/* RW-1685 — All | Mentions tabs (the platform-wide mentions inbox) */}
              <div style={{ display: 'inline-flex', background: colors.line2, borderRadius: 8, padding: 2, gap: 1, marginLeft: 6 }}>
                {[['all', 'All'], ['mentions', '@ Mentions']].map(([id, label]) => (
                  <button key={id} onClick={() => setTab(id)} aria-pressed={tab === id}
                    style={{
                      padding: '3px 9px', borderRadius: 6, border: 'none', cursor: 'pointer',
                      fontSize: 11, fontWeight: 600, fontFamily: fonts.body,
                      background: tab === id ? '#fff' : 'transparent',
                      color: tab === id ? colors.coralInk : colors.muted,
                      boxShadow: tab === id ? '0 1px 2px rgba(0,0,0,.08)' : 'none',
                    }}>{label}</button>
                ))}
              </div>
            </div>
            {unreadCount > 0 ? (
              /* deck .ghost — the dashed pill affordance */
              <button onClick={markAllRead} style={{ border: `1px dashed ${colors.line}`, borderRadius: 999, padding: '3px 11px', fontSize: 11.5, color: colors.muted, background: 'transparent', cursor: 'pointer', fontFamily: fonts.body }}>Mark all read</button>
            ) : null}
          </div>
          <div style={{ maxHeight: 440, overflowY: 'auto' }}>
            {rows.length === 0 ? (
              <div style={{ padding: 32, textAlign: 'center', color: colors.mute2, fontSize: 13 }}>
                {tab === 'mentions' ? 'No mentions yet' : 'No notifications yet'}
              </div>
            ) : rows.map((row) => (row.kind === 'group' ? renderGroup(row) : renderItem(row.n)))}
          </div>
          <div style={{ padding: '9px 14px', borderTop: `1px solid ${colors.line}`, textAlign: 'center' }}>
            <button onClick={() => { setOpen(false); navigate('/notifications'); }}
              style={{ background: 'none', border: 'none', color: colors.coralInk, fontSize: 11.5, fontWeight: 600, cursor: 'pointer', fontFamily: fonts.body }}>
              View all →
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
